import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Accept } from 'react-dropzone';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../../api/axios.js';
import { API_ENDPOINTS } from '../../../api/endpoints.js';
import { AppSelect } from '../../../shared/common/AppSelect.tsx';
import { BulkDocumentUploadZone } from '../../../components/bulk/BulkDocumentUploadZone.tsx';
import { BulkExtractionProgressOverlay } from '../../../components/bulk/BulkExtractionProgressOverlay.tsx';
import { Tooltip, TooltipProvider } from '../../../admin/components/shared/Tooltip.tsx';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import { importRisksBulkUpload } from '../../../request/risks/risks.api.ts';
import type { RiskBulkImportFileDataRow } from '../../../request/risks/risks.types.ts';
import { queryKeys } from '../../../queryKeys.ts';
import { TENANT_PATHS } from '../../../routing/paths.ts';
import { uploadFilesAndComplete } from '../../../utils/fileUpload.js';
import { MODULE_UPLOAD } from '../../../utils/moduleUpload.ts';
import {
  riskDraftRowHasAnyMergedError,
  selectedRiskDraftRowsHaveMergedErrors,
  stripRiskBackendErrorsForMappedField,
  type RiskDraftTableField,
  type RiskDraftValidationRow,
} from '../riskDraftValidation.ts';
import { RiskDraftReviewTable } from './RiskDraftReviewTable.tsx';
import { mapProcessValidationErrorsByFileDataIndex } from '../../../components/processes/validations.js';
import {
  RISK_CATEGORY_DRAFT_OPTIONS,
  RISK_LEVEL_SELECT_OPTIONS,
  normalizeRiskCategoryForDraft,
  normalizeRiskLevelForDraft,
} from '../riskDraftSelectMaps.ts';

const SUPPORTED_EXTENSIONS = ['xlsx', 'csv'];

/** Same copy as process bulk upload — do not surface raw API header_errors in the dropzone. */
const RISK_BULK_HEADER_VALIDATION_ERROR_MESSAGE =
  'Header validation failed. Please verify the file headers match the sample document and try again.';

const RISK_BULK_ACCEPT: Accept = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
};

const DRAFT_TABLE_PAGE_SIZE = 10;
const DRAFT_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50];

const IMPORT_RISKS_VALIDATION_TOOLTIP = 'Before importing, please fix validation errors on the selected rows.';

const DRAFT_LEVEL_FILTER_OPTIONS = [
  { value: 'all', label: 'All levels' },
  ...RISK_LEVEL_SELECT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

const DRAFT_CATEGORY_FILTER_OPTIONS = [
  { value: 'all', label: 'All categories' },
  ...RISK_CATEGORY_DRAFT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

const DRAFT_VALIDITY_FILTER_OPTIONS = [
  { value: 'all', label: 'All rows' },
  { value: 'invalid', label: 'Invalid rows' },
  { value: 'valid', label: 'Valid rows' },
] as const;

const DRAFT_ROW_VALIDATION_OPTS = { showBackendErrors: true, applyFrontendValidation: true } as const;

function defaultSelectedRiskIdsForValidRows(rows: RiskDraftValidationRow[]): Set<string> {
  const getRowVal = (r: RiskDraftValidationRow, f: RiskDraftTableField): unknown => r[f];
  return new Set(
    rows
      .filter((r) => !riskDraftRowHasAnyMergedError(r, getRowVal, DRAFT_ROW_VALIDATION_OPTS))
      .map((r) => r.id)
  );
}

type BulkOverlayState = {
  open: boolean;
  statusMessage: string;
  detailMessage: string | null;
  isComplete: boolean;
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const normalizeHeaderKey = (key: string): string =>
  String(key || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const getFirstValue = (row: Record<string, unknown> | undefined, keys: string[]): string => {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) {
      return String(row[key]).trim();
    }
  }
  const entries = Object.entries(row);
  for (const key of keys) {
    const want = normalizeHeaderKey(key);
    const hit = entries.find(([k]) => normalizeHeaderKey(k) === want);
    if (hit && hit[1] !== undefined && hit[1] !== null) {
      return String(hit[1]).trim();
    }
  }
  return '';
};

type RiskFileValidationRow = {
  row_number?: number;
  file_data_index?: number;
  passed?: boolean;
  errors?: Array<{ column?: string; reason?: string; value?: string }>;
};

type RiskFileValidationResponse = {
  file_type?: string;
  file_data?: Array<Record<string, unknown>>;
  header_valid?: boolean;
  header_errors?: string[];
  total_rows?: number;
  passed_rows?: number;
  failed_rows?: number;
  error_rows?: RiskFileValidationRow[];
};

function mapFileDataToRiskDraftRows(fileData: Array<Record<string, unknown>> | undefined): RiskDraftValidationRow[] {
  return (Array.isArray(fileData) ? fileData : []).map((row, index) => ({
    id: `risk-upload-${Date.now()}-${index}`,
    clientRiskId: getFirstValue(row, ['Client Risk ID', 'client_risk_id', 'ClientRiskID']),
    riskDescription: getFirstValue(row, ['Risk Description', 'risk_description', 'RiskDescription']),
    riskLevel: normalizeRiskLevelForDraft(getFirstValue(row, ['Risk Level', 'risk_level', 'RiskLevel'])),
    riskCategory: normalizeRiskCategoryForDraft(getFirstValue(row, ['Risk Category', 'risk_category', 'RiskCategory'])),
  }));
}

function mergeRiskDraftErrors(
  rows: RiskDraftValidationRow[],
  errorRows: RiskFileValidationRow[] | undefined
): RiskDraftValidationRow[] {
  const byIndex = mapProcessValidationErrorsByFileDataIndex(errorRows);
  return rows.map((row, index) => ({
    ...row,
    validation_errors: byIndex[index] || [],
  }));
}

function riskRowToImportWire(
  row: RiskDraftValidationRow,
  getFieldValue: (r: RiskDraftValidationRow, f: RiskDraftTableField) => unknown
): RiskBulkImportFileDataRow {
  return {
    client_risk_id: String(getFieldValue(row, 'clientRiskId') ?? ''),
    risk_description: String(getFieldValue(row, 'riskDescription') ?? ''),
    risk_level: String(getFieldValue(row, 'riskLevel') ?? ''),
    risk_category: String(getFieldValue(row, 'riskCategory') ?? ''),
  };
}

const RISK_FILE_MODULE_UPLOAD = MODULE_UPLOAD.SOX_PROCESS;

export const RiskBulkUploadSection: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [bulkOverlay, setBulkOverlay] = useState<BulkOverlayState>({
    open: false,
    statusMessage: '',
    detailMessage: null,
    isComplete: false,
  });
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadHeaderErrorMessage, setUploadHeaderErrorMessage] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<RiskFileValidationResponse | null>(null);
  const [draftRows, setDraftRows] = useState<RiskDraftValidationRow[]>([]);
  const [riskEdited, setRiskEdited] = useState<Record<string, Partial<Record<RiskDraftTableField, string>>>>({});
  const [riskSelectedIds, setRiskSelectedIds] = useState<Set<string>>(new Set());
  const [riskEditingField, setRiskEditingField] = useState<{ riskId: string; field: string } | null>(null);
  const [stagedFileId, setStagedFileId] = useState<string | null>(null);
  const [draftSearchQuery, setDraftSearchQuery] = useState('');
  const [draftLevelFilter, setDraftLevelFilter] = useState('all');
  const [draftCategoryFilter, setDraftCategoryFilter] = useState('all');
  const [draftValidityFilter, setDraftValidityFilter] = useState<'all' | 'invalid' | 'valid'>('all');

  const importMutation = useMutation({
    mutationFn: importRisksBulkUpload,
    onSuccess: async (data: { partial_success?: boolean; message?: string } | unknown) => {
      const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
      if (body.partial_success) {
        toast.success(String(body.message || 'Risks imported with some rows skipped.'));
      } else {
        toast.success(String(body.message || 'Risks imported successfully.'));
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });
      navigate(TENANT_PATHS.SOX_RISKS);
    },
    onError: (error: Error) => {
      const err = error as { response?: { data?: { error?: string; validation?: RiskFileValidationResponse } } };
      const validation = err?.response?.data?.validation;
      if (validation && typeof validation === 'object') {
        setValidationResult(validation);
        if (validation.header_valid && Array.isArray(validation.file_data)) {
          const merged = mergeRiskDraftErrors(
            mapFileDataToRiskDraftRows(validation.file_data),
            validation.error_rows
          );
          setDraftRows(merged);
          setRiskSelectedIds(defaultSelectedRiskIdsForValidRows(merged));
        } else {
          setDraftRows([]);
          setRiskSelectedIds(new Set());
        }
        setRiskEdited({});
        setRiskEditingField(null);
        resetDraftFilters();
      }
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Import failed.')));
    },
  });

  const getRiskFieldValue = React.useCallback(
    (row: RiskDraftValidationRow, field: RiskDraftTableField): unknown => {
      const edits = riskEdited[row.id];
      if (edits && edits[field] !== undefined) return edits[field];
      return row[field];
    },
    [riskEdited]
  );

  const updateRiskField = (riskId: string, field: string, value: string) => {
    const f = field as RiskDraftTableField;
    setRiskEdited((prev) => ({
      ...prev,
      [riskId]: { ...(prev[riskId] || {}), [f]: value },
    }));
    setDraftRows((rows) =>
      rows.map((r) => {
        if (r.id !== riskId) return r;
        const stripped = stripRiskBackendErrorsForMappedField(r.validation_errors, f);
        return stripped === r.validation_errors ? r : { ...r, validation_errors: stripped };
      })
    );
  };

  const isRiskFieldEditing = (riskId: string, field: string) =>
    riskEditingField?.riskId === riskId && riskEditingField?.field === field;

  const startRiskEditing = (riskId: string, field: string) => {
    setRiskEditingField({ riskId, field });
  };

  const stopRiskEditing = () => {
    setTimeout(() => setRiskEditingField(null), 100);
  };

  const validationOpts = useMemo(
    () => ({
      showBackendErrors: true,
      applyFrontendValidation: true,
    }),
    []
  );

  const selectedHasValidationErrors = useMemo(
    () =>
      selectedRiskDraftRowsHaveMergedErrors(draftRows, riskSelectedIds, getRiskFieldValue, {
        showBackendErrors: validationOpts.showBackendErrors,
        applyFrontendValidation: validationOpts.applyFrontendValidation,
      }),
    [draftRows, riskSelectedIds, getRiskFieldValue, validationOpts]
  );

  useEffect(() => {
    if (draftRows.length === 0) return;
    setRiskSelectedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of Array.from(prev)) {
        const row = draftRows.find((r) => r.id === id);
        if (!row) {
          next.delete(id);
          changed = true;
          continue;
        }
        if (riskDraftRowHasAnyMergedError(row, getRiskFieldValue, validationOpts)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [draftRows, riskEdited, getRiskFieldValue, validationOpts]);

  const displayedDraftRows = useMemo(() => {
    let rows = draftRows;
    const q = draftSearchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((row) => {
        const id = String(getRiskFieldValue(row, 'clientRiskId') ?? '').toLowerCase();
        const desc = String(getRiskFieldValue(row, 'riskDescription') ?? '').toLowerCase();
        const lev = String(getRiskFieldValue(row, 'riskLevel') ?? '').toLowerCase();
        const cat = String(getRiskFieldValue(row, 'riskCategory') ?? '').toLowerCase();
        return id.includes(q) || desc.includes(q) || lev.includes(q) || cat.includes(q);
      });
    }
    if (draftLevelFilter !== 'all') {
      rows = rows.filter((r) => String(getRiskFieldValue(r, 'riskLevel') ?? '') === draftLevelFilter);
    }
    if (draftCategoryFilter !== 'all') {
      rows = rows.filter((r) => String(getRiskFieldValue(r, 'riskCategory') ?? '') === draftCategoryFilter);
    }
    if (draftValidityFilter === 'invalid') {
      rows = rows.filter((r) => riskDraftRowHasAnyMergedError(r, getRiskFieldValue, validationOpts));
    } else if (draftValidityFilter === 'valid') {
      rows = rows.filter((r) => !riskDraftRowHasAnyMergedError(r, getRiskFieldValue, validationOpts));
    }
    return rows;
  }, [
    draftRows,
    draftSearchQuery,
    draftLevelFilter,
    draftCategoryFilter,
    draftValidityFilter,
    getRiskFieldValue,
    validationOpts,
  ]);

  const draftFiltersActive =
    draftSearchQuery.trim() !== '' ||
    draftLevelFilter !== 'all' ||
    draftCategoryFilter !== 'all' ||
    draftValidityFilter !== 'all';

  const resetDraftFilters = useCallback(() => {
    setDraftSearchQuery('');
    setDraftLevelFilter('all');
    setDraftCategoryFilter('all');
    setDraftValidityFilter('all');
  }, []);

  const resetRiskBulkUploadSession = useCallback(() => {
    setDraftRows([]);
    setRiskEdited({});
    setRiskSelectedIds(new Set());
    setRiskEditingField(null);
    setStagedFileId(null);
    setValidationResult(null);
    setUploadHeaderErrorMessage(null);
    resetDraftFilters();
    setBulkOverlay((prev) => ({ ...prev, open: false, isComplete: false }));
    toast.success('Review cleared. Upload a new spreadsheet when you are ready.');
  }, [resetDraftFilters]);

  const handleDownloadRiskSample = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.getSampleExcelTemplate('risk'), { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'risk_upload_sample.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Risk Template download failed:', error);
      toast.error(getApiErrorMessage(error as Error, 'Failed to download sample'));
    }
  };

  const runRiskBulkUpload = async (acceptedFiles: File[]) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    const ext = file.name?.split('.').pop()?.toLowerCase() || '';
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      toast.error('Use .xlsx or .csv');
      return;
    }

    setUploadHeaderErrorMessage(null);
    setBulkOverlay({
      open: true,
      statusMessage: 'Uploading file…',
      detailMessage: file.name,
      isComplete: false,
    });
    setUploadBusy(true);
    setStagedFileId(null);

    try {
      const userHash = localStorage.getItem('userHash') || '';
      const [uploaded] = await uploadFilesAndComplete([file], userHash, RISK_FILE_MODULE_UPLOAD);
      const fileId = uploaded?.file_id;
      if (!fileId) {
        throw new Error('Upload did not return file id');
      }

      setBulkOverlay((prev) => ({
        ...prev,
        statusMessage: 'Validating spreadsheet…',
        detailMessage: 'Checking headers and row data.',
      }));

      const { data } = await axiosInstance.post<RiskFileValidationResponse>('/api/risk-upload/validate', {
        file_id: fileId,
      });

      if (!data?.header_valid) {
        toast.error(RISK_BULK_HEADER_VALIDATION_ERROR_MESSAGE);
        setUploadHeaderErrorMessage(RISK_BULK_HEADER_VALIDATION_ERROR_MESSAGE);
        setValidationResult(null);
        setDraftRows([]);
        setRiskEdited({});
        setRiskSelectedIds(new Set());
        setRiskEditingField(null);
        setStagedFileId(null);
        resetDraftFilters();
        setBulkOverlay((prev) => ({ ...prev, open: false }));
        return;
      }

      setStagedFileId(fileId);
      setValidationResult(data);
      const mapped = mergeRiskDraftErrors(mapFileDataToRiskDraftRows(data.file_data), data.error_rows);
      setDraftRows(mapped);
      resetDraftFilters();
      setRiskEdited({});
      setRiskSelectedIds(defaultSelectedRiskIdsForValidRows(mapped));
      setRiskEditingField(null);

      setBulkOverlay((prev) => ({
        ...prev,
        statusMessage: 'Ready to review',
        detailMessage: null,
        isComplete: true,
      }));
      await sleep(550);
      setBulkOverlay((prev) => ({ ...prev, open: false, isComplete: false }));

      if (Number(data?.failed_rows || 0) > 0) {
        toast.error('Some rows failed validation. Review the highlighted cells before importing.');
      } else {
        toast.success('File validated. Select rows to import, or edit cells as needed.');
      }
    } catch (error) {
      console.error('Risk upload validate failed:', error);
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error as Error, 'Validate failed')));
      setUploadHeaderErrorMessage(null);
      setValidationResult(null);
      setDraftRows([]);
      setRiskEdited({});
      setRiskSelectedIds(new Set());
      setRiskEditingField(null);
      setStagedFileId(null);
      resetDraftFilters();
      setBulkOverlay((prev) => ({ ...prev, open: false, isComplete: false }));
    } finally {
      setUploadBusy(false);
    }
  };

  const handleImport = () => {
    if (riskSelectedIds.size === 0) {
      toast.error('Select at least one risk row to import.');
      return;
    }
    if (selectedHasValidationErrors) {
      toast.error('Fix validation issues on selected rows before importing.');
      return;
    }

    const selected = draftRows.filter((r) => riskSelectedIds.has(r.id));
    const file_data = selected.map((row) => riskRowToImportWire(row, getRiskFieldValue));
    importMutation.mutate({
      ...(stagedFileId ? { file_id: stagedFileId } : {}),
      file_data,
    });
  };

  const isImporting = importMutation.isPending;

  const isImportDisabled =
    isImporting || riskSelectedIds.size === 0 || selectedHasValidationErrors || !validationResult?.header_valid;

  const showImportValidationTooltip = riskSelectedIds.size > 0 && selectedHasValidationErrors;

  return (
    <div className="mt-5">
      <BulkExtractionProgressOverlay
        open={bulkOverlay.open}
        title="Processing Bulk Upload"
        subtitle="We are uploading your spreadsheet and validating each row before you review and import risks."
        statusMessage={bulkOverlay.statusMessage}
        detailMessage={bulkOverlay.detailMessage}
        isComplete={bulkOverlay.isComplete}
      />

      {draftRows.length === 0 ? (
        <BulkDocumentUploadZone
          accept={RISK_BULK_ACCEPT}
          multiple={false}
          disabled={uploadBusy || isImporting}
          sectionTitle="Bulk upload risks"
          sectionSubtitle="Upload a spreadsheet (.xlsx or .csv). We validate the file, map rows to risks, and show them below for review before you import them."
          cardTitle="Upload risk spreadsheet"
          cardSubtitle="Drop a file here or choose one from your computer. Same flow as process bulk upload."
          formatsFootnote="Supports Excel (.xlsx) and CSV (.csv)."
          chooseFilesLabel="Choose Files"
          onDownloadSample={handleDownloadRiskSample}
          onDropAccepted={runRiskBulkUpload}
          inlineAlertMessage={uploadHeaderErrorMessage}
        />
      ) : null}

      {draftRows.length > 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-6 py-4">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-medium text-gray-900">
                {riskSelectedIds.size > 0
                  ? `${riskSelectedIds.size} of ${draftRows.length} selected`
                  : `Extracted Risks (${draftRows.length})`}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Review the extracted rows, fix any highlighted validation issues, then select the risks you want to import.
              </p>
            </div>
            <button
              type="button"
              onClick={resetRiskBulkUploadSession}
              disabled={uploadBusy || isImporting}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Clear uploaded file and return to upload step"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Upload New
            </button>
          </div>

          <div className="border-b border-gray-200 bg-gray-50/90 px-4 py-3 sm:px-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between lg:gap-4">
              <div className="relative min-w-0 flex-1 lg:max-w-md">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <input
                  type="search"
                  placeholder="Search by reference ID, description, level, or category…"
                  value={draftSearchQuery}
                  onChange={(e) => setDraftSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  aria-label="Search extracted risks"
                />
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <AppSelect
                  wrapperClassName="min-w-[9.5rem]"
                  size="control"
                  value={draftLevelFilter}
                  onChange={(e) => setDraftLevelFilter(e.target.value)}
                  aria-label="Filter by risk level"
                  options={DRAFT_LEVEL_FILTER_OPTIONS}
                />
                <AppSelect
                  wrapperClassName="min-w-[9.5rem]"
                  size="control"
                  value={draftCategoryFilter}
                  onChange={(e) => setDraftCategoryFilter(e.target.value)}
                  aria-label="Filter by risk category"
                  options={DRAFT_CATEGORY_FILTER_OPTIONS}
                />
                <AppSelect
                  wrapperClassName="min-w-[11rem]"
                  size="control"
                  value={draftValidityFilter}
                  onChange={(e) => setDraftValidityFilter(e.target.value as 'all' | 'invalid' | 'valid')}
                  aria-label="Filter by validation status"
                  options={[...DRAFT_VALIDITY_FILTER_OPTIONS]}
                />
                {draftFiltersActive ? (
                  <button
                    type="button"
                    onClick={resetDraftFilters}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>
            </div>
            {displayedDraftRows.length !== draftRows.length ? (
              <p className="mt-2 text-xs text-gray-500">
                Showing {displayedDraftRows.length} of {draftRows.length} rows
              </p>
            ) : null}
          </div>

          <RiskDraftReviewTable
            rows={displayedDraftRows}
            selectedIds={riskSelectedIds}
            onToggleRow={(riskId, checked) => {
              if (checked) {
                const row = draftRows.find((r) => r.id === riskId);
                if (row && riskDraftRowHasAnyMergedError(row, getRiskFieldValue, validationOpts)) {
                  return;
                }
              }
              setRiskSelectedIds((prev) => {
                const next = new Set(prev);
                if (checked) next.add(riskId);
                else next.delete(riskId);
                return next;
              });
            }}
            rowSelectAriaLabel={(row) => `Select risk ${getRiskFieldValue(row, 'riskDescription') || row.id}`}
            getFieldValue={getRiskFieldValue}
            isFieldEditing={isRiskFieldEditing}
            startEditing={startRiskEditing}
            stopEditing={stopRiskEditing}
            updateField={updateRiskField}
            showBackendValidationErrors
            applyFrontendValidation
            initialPageSize={DRAFT_TABLE_PAGE_SIZE}
            pageSizeOptions={DRAFT_TABLE_PAGE_SIZE_OPTIONS}
          />

          <div className="flex items-center justify-end border-t border-gray-200 px-6 py-3">
            <TooltipProvider>
              {showImportValidationTooltip ? (
                <Tooltip content={IMPORT_RISKS_VALIDATION_TOOLTIP} side="top">
                  <span className="inline-flex">
                    <button
                      type="button"
                      onClick={() => void handleImport()}
                      disabled={isImportDisabled}
                      className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {isImporting ? 'Importing…' : 'Import selected risks'}
                    </button>
                  </span>
                </Tooltip>
              ) : (
                <button
                  type="button"
                  title={
                    riskSelectedIds.size === 0
                      ? 'Select at least one row to import.'
                      : !validationResult?.header_valid
                        ? 'File did not pass header validation.'
                        : undefined
                  }
                  onClick={() => void handleImport()}
                  disabled={isImportDisabled}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {isImporting ? 'Importing…' : 'Import selected risks'}
                </button>
              )}
            </TooltipProvider>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RiskBulkUploadSection;
